import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderInput, Search, ArrowRight } from "lucide-react";

/**
 * Sposta un gruppo di macchine da una categoria a un'altra.
 *
 * Serve quando una categoria va rimessa in ordine: si sceglie da dove partire,
 * si guarda l'elenco vero delle targhe e si spunta cosa spostare. La conferma
 * la dà chi conosce i mezzi, guardando il dato reale, non chi scrive il codice.
 */
export default function SpostaCategoria({ categorie, onDone }) {
  const [aperto, setAperto] = useState(false);
  const [mezzi, setMezzi] = useState([]);
  const [caricando, setCaricando] = useState(false);
  const [origine, setOrigine] = useState("");
  const [destinazione, setDestinazione] = useState("");
  const [scelti, setScelti] = useState([]);
  const [cerca, setCerca] = useState("");
  const [busy, setBusy] = useState(false);
  const [esito, setEsito] = useState(null);

  useEffect(() => {
    if (!aperto) return;
    setCaricando(true);
    api.get("/equipment")
      .then(({ data }) => setMezzi(data))
      .catch(() => toast.error("Errore nel caricamento dei mezzi"))
      .finally(() => setCaricando(false));
  }, [aperto]);

  const elenco = useMemo(() => {
    const q = cerca.trim().toUpperCase();
    return mezzi
      .filter(m => !origine || (m.category || "") === origine)
      .filter(m => !q || [m.identifier, m.brand, m.model, m.tipo]
        .filter(Boolean).join(" ").toUpperCase().includes(q))
      .sort((a, b) => (a.identifier || "").localeCompare(b.identifier || ""));
  }, [mezzi, origine, cerca]);

  const spunta = (id) =>
    setScelti(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Stessa normalizzazione dell'import dei libri: solo lettere e cifre, cosi'
  // "DE 118 HT", "de118ht" e "DE-118-HT" sono la stessa targa.
  const soloLettereCifre = (t) => String(t || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

  const [targheIncollate, setTargheIncollate] = useState("");
  const [nonTrovate, setNonTrovate] = useState([]);

  const spuntaDaElenco = () => {
    const cercate = targheIncollate.split(/[\s,;]+/).map(soloLettereCifre).filter(Boolean);
    if (cercate.length === 0) { toast.error("Incolla almeno una targa"); return; }
    const perTarga = new Map(mezzi.map(m => [soloLettereCifre(m.identifier), m.id]));
    const trovati = [];
    const mancanti = [];
    for (const t of cercate) {
      const id = perTarga.get(t);
      if (id) trovati.push(id); else mancanti.push(t);
    }
    setScelti(Array.from(new Set(trovati)));
    setNonTrovate(mancanti);
    toast.success(`${trovati.length} targhe spuntate`
      + (mancanti.length ? `, ${mancanti.length} non trovate` : ""));
  };

  const azzera = () => {
    setScelti([]); setCerca(""); setEsito(null);
    setTargheIncollate(""); setNonTrovate([]);
  };
  const chiudi = () => { setAperto(false); azzera(); setOrigine(""); setDestinazione(""); };

  const sposta = async () => {
    if (!destinazione) { toast.error("Scegli la categoria di destinazione"); return; }
    if (scelti.length === 0) { toast.error("Non hai spuntato nessun mezzo"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/equipment/cambia-categoria",
        { ids: scelti, categoria: destinazione });
      setEsito(data);
      toast.success(`${data.spostate} mezzi spostati in ${destinazione}`);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nello spostamento");
    } finally { setBusy(false); }
  };

  return (<>
    <Button variant="outline" className="h-12" onClick={() => setAperto(true)}
            data-testid="sposta-categoria-btn">
      <FolderInput className="w-4 h-4 mr-2" /> Sposta di categoria
    </Button>

    {aperto && (
      <Dialog open onOpenChange={chiudi}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Sposta mezzi di categoria</DialogTitle></DialogHeader>

          {esito ? (
            <div className="space-y-3">
              <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3">
                <b>{esito.spostate}</b> mezzi spostati in <b>{esito.categoria}</b>.
                {esito.gia_in_categoria > 0 &&
                  ` ${esito.gia_in_categoria} erano già lì e non sono stati toccati.`}
              </div>
              {esito.dettagli.length > 0 && (
                <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-72 overflow-y-auto">
                  {esito.dettagli.map((d, i) => (
                    <div key={i} className="p-2 text-sm flex items-center gap-2">
                      <span className="font-mono font-semibold">{d.identificativo}</span>
                      <span className="text-slate-400 text-xs">{d.da}</span>
                      <ArrowRight className="w-3 h-3 text-slate-400" />
                      <span className="text-xs">{esito.categoria}</span>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={azzera}>Sposta altri</Button>
                <Button onClick={chiudi} className="bg-orange-500 hover:bg-orange-600 text-white">
                  Chiudi
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Mostra i mezzi di</Label>
                  <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                          value={origine}
                          onChange={(e) => { setOrigine(e.target.value); setScelti([]); }}>
                    <option value="">Tutte le categorie</option>
                    {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Spostali in *</Label>
                  <select className="w-full h-10 border border-slate-200 rounded px-3 mt-1"
                          value={destinazione} onChange={(e) => setDestinazione(e.target.value)}>
                    <option value="">Scegli categoria...</option>
                    {categorie.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input className="pl-9" placeholder="Cerca targa, marca, modello..."
                       value={cerca} onChange={(e) => setCerca(e.target.value)} />
              </div>

              <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
                <Label className="text-xs">Scorciatoia · spunta incollando le targhe</Label>
                <textarea rows={2}
                          className="w-full border border-slate-200 rounded px-3 py-2 text-sm font-mono"
                          placeholder="DE118HT GL199RH GR466HV..."
                          value={targheIncollate}
                          onChange={(e) => setTargheIncollate(e.target.value)} />
                <div className="flex items-center gap-2 flex-wrap">
                  <Button type="button" variant="outline" size="sm" onClick={spuntaDaElenco}>
                    Spunta queste targhe
                  </Button>
                  <span className="text-[11px] text-slate-500">
                    Separate da spazi, virgole o a capo. Spazi e trattini non contano.
                  </span>
                </div>
                {nonTrovate.length > 0 && (
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    Non trovate in anagrafica: <b>{nonTrovate.join(", ")}</b>
                  </div>
                )}
              </div>

              {caricando ? (
                <p className="text-sm text-slate-500 py-6 text-center">Caricamento...</p>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    {/* Incollando le targhe si puo' spuntare un mezzo che il
                        filtro qui sopra non mostra: va detto, altrimenti il
                        conto degli spuntati sembra sbagliato. */}
                    <span>
                      {elenco.length} mezzi · {scelti.length} spuntati
                      {(() => {
                        const fuori = scelti.filter(id => !elenco.some(m => m.id === id)).length;
                        return fuori > 0 ? ` (${fuori} fuori da questo filtro)` : "";
                      })()}
                    </span>
                    <div className="flex gap-2">
                      <button type="button" className="text-orange-600 hover:underline"
                              onClick={() => setScelti(elenco.map(m => m.id))}>
                        Spunta tutti
                      </button>
                      <button type="button" className="text-orange-600 hover:underline"
                              onClick={() => setScelti([])}>
                        Nessuno
                      </button>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-80 overflow-y-auto">
                    {elenco.length === 0 && (
                      <p className="p-4 text-sm text-slate-500 text-center">Nessun mezzo</p>
                    )}
                    {elenco.map(m => (
                      <label key={m.id}
                             className="p-2 flex items-center gap-3 cursor-pointer hover:bg-slate-50">
                        <input type="checkbox" checked={scelti.includes(m.id)}
                               onChange={() => spunta(m.id)} />
                        <span className="font-mono font-semibold text-sm w-24 flex-shrink-0">
                          {m.identifier}
                        </span>
                        <span className="text-sm text-slate-600 flex-1 truncate">
                          {[m.brand, m.model, m.tipo].filter(Boolean).join(" ") || "—"}
                        </span>
                        <span className="text-[11px] text-slate-400 flex-shrink-0">{m.category}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={chiudi}>Annulla</Button>
                <Button onClick={sposta} disabled={busy || scelti.length === 0 || !destinazione}
                        className="bg-orange-500 hover:bg-orange-600 text-white">
                  {busy ? "Spostamento..." : `Sposta ${scelti.length} mezzi`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    )}
  </>);
}
